import React, { useState, useEffect } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import formatValue from "../../../../../utils/formatValue";
import {
  Container,
  ButtonsContainer,
  SliderContainer,
  ValuesToSearch,
  StyledSlider,
  StyledThumb,
  StyledTrack,
  TotalCheckboxContainer,
  PropertyBenefitsContent,
  TitleCloseWrapper,
  Title,
  CategoriesCardsWrapper,
  CategoryCard,
  DefaultWrapper,
  CharacteristicNameValueWrapper,
  CharacteristicName,
  CharacteristicValue,
  DefaultTitle,
  InputsWrapper,
  InputWrapper,
  InputLabel,
  InputField,
  RangeErrorMessage,
} from "./styles";
import { FiX } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import {
  CHARACTERISTIC_NAMES,
  ORDER_BY_OPTIONS,
  PROPERTY_TYPES,
} from "./constants";
// IMPORTANTE: Certifique-se que a interface Filter aqui está com os tipos corretos
import { Filter } from "../../../../../global/types"; 
import { FaMinus, FaPlus } from "react-icons/fa";
import CustomButton from "../../../../../components/CustomButton";
import { SortOptions } from "../../../enum";
import {
  AREA_BOUNDS,
  DEFAULT_AREA_RANGE,
  DEFAULT_RENT_RANGE,
  getAreaRangeForSlider,
  getRentRangeForSlider,
  isInvertedRangeInput,
  resolveAreaRangeFromInputs,
  resolveRentRangeFromInputs,
  RENT_BOUNDS,
} from "./searchFilterRangeUtils";

const INVERTED_RANGE_ERROR_MESSAGE =
  "O valor máximo deve ser maior ou igual ao mínimo.";

interface IProps {
  open: boolean;
  onClose(): void;
  isLoadingApiRequest: boolean;
  onSortProperty: (option: SortOptions) => void;
}

type SearchFilters = Omit<Filter, "rentValue" | "area"> & {
  rentValue: [number, number];
  area: [number, number];
  rooms: number;
  garages: number;
  bathrooms: number;
  [key: string]: any;
};

export default function SearchFilter({
  open,
  onClose,
  onSortProperty,
}: IProps) {
  const history = useHistory();

  
  const getFiltersFromUrl = (): SearchFilters => {
    const urlParams = new URLSearchParams(window.location.search);

    
    const getNumberFromUrl = (paramName: string): number | undefined => {
      const value = urlParams.get(paramName);
      const num = value ? Number(value) : undefined;
      return num !== undefined && !isNaN(num) ? num : undefined;
    };

    const getBooleanFromUrl = (paramName: string): boolean => {
      const value = urlParams.get(paramName);
      return value === "true"; 
    };

    const getArrayFromUrl = (
      paramName: string,
      defaultValue: [number, number]
    ): [number, number] => {
      const value = urlParams.get(paramName);
      if (!value) return defaultValue;

      const parsed = value
        .split(",")
        .map(Number)
        .filter((num) => !Number.isNaN(num));

      if (parsed.length !== 2) return defaultValue;
      return [parsed[0], parsed[1]];
    };

    const filters: SearchFilters = {
      condominium: urlParams.get("condominium") || undefined,
      uf: urlParams.get("UF") || undefined,
      city: urlParams.get("city") || undefined,
      neighborhood: urlParams.get("neighborhood") || undefined,

      type: getNumberFromUrl("imovelType"), 
      
      rentValue: getArrayFromUrl("values", [400, 20000]),
      area: getArrayFromUrl("area", [20, 1000]),

      
      rooms: getNumberFromUrl("rooms") ?? 0, 
      garages: getNumberFromUrl("garages") ?? 0, 
      bathrooms: getNumberFromUrl("bathrooms") ?? 0, 

      page: getNumberFromUrl("page"),
      total: getBooleanFromUrl("total"),
      rentValueOrder: urlParams.get("rentValueOrder") || undefined,
      minimal: getNumberFromUrl("minimal") ?? 1,

      furnished: getBooleanFromUrl("furnished"),
      has_sea_view: getBooleanFromUrl("has_sea_view"),
      has_ac: getBooleanFromUrl("has_ac"),
      has_porch: getBooleanFromUrl("has_porch"),
      has_storage: getBooleanFromUrl("has_storage"),
      has_elevator: getBooleanFromUrl("has_elevator"),
      has_gym: getBooleanFromUrl("has_gym"),
      has_pool: getBooleanFromUrl("has_pool"),
      has_sports: getBooleanFromUrl("has_sports"),
      has_playground: getBooleanFromUrl("has_playground"),
      has_bbq: getBooleanFromUrl("has_bbq"),
      has_party: getBooleanFromUrl("has_party"),
      has_game_room: getBooleanFromUrl("has_game_room"),
      has_garden: getBooleanFromUrl("has_garden"),
      has_24h_concierge: getBooleanFromUrl("has_24h_concierge"),
    };
    return filters;
  };

  const initialFilters = getFiltersFromUrl();

  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [rentMinInput, setRentMinInput] = useState(
    String(initialFilters.rentValue[0])
  );
  const [rentMaxInput, setRentMaxInput] = useState(
    String(initialFilters.rentValue[1])
  );
  const [areaMinInput, setAreaMinInput] = useState(
    String(initialFilters.area[0])
  );
  const [areaMaxInput, setAreaMaxInput] = useState(
    String(initialFilters.area[1])
  );
  const [sortOption, setSortOption] = useState<SortOptions>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderByParam = urlParams.get("orderBy");
    return orderByParam && ORDER_BY_OPTIONS.includes(orderByParam as SortOptions)
      ? (orderByParam as SortOptions)
      : SortOptions.NO_PREFERENCE;
  });

  function syncNumericInputsFromFilters(nextFilters: SearchFilters) {
    setRentMinInput(String(nextFilters.rentValue[0]));
    setRentMaxInput(String(nextFilters.rentValue[1]));
    setAreaMinInput(String(nextFilters.area[0]));
    setAreaMaxInput(String(nextFilters.area[1]));
  }

  function resolveValidatedRanges() {
    return {
      rentValue: resolveRentRangeFromInputs(rentMinInput, rentMaxInput),
      area: resolveAreaRangeFromInputs(areaMinInput, areaMaxInput),
    };
  }

  useEffect(() => {
    if (open) {
      const urlFilters = getFiltersFromUrl();
      setFilters(urlFilters);
      syncNumericInputsFromFilters(urlFilters);
      const urlParams = new URLSearchParams(window.location.search);
      const orderByParam = urlParams.get("orderBy");
      if (orderByParam && ORDER_BY_OPTIONS.includes(orderByParam as SortOptions)) {
        setSortOption(orderByParam as SortOptions);
      } else {
        setSortOption(SortOptions.NO_PREFERENCE);
      }
    }
  }, [open]);

  function handleApplyFilters() {
    if (!(filters.uf || filters.city)) return;

    const isRentRangeInverted = isInvertedRangeInput(
      rentMinInput,
      rentMaxInput,
      RENT_BOUNDS
    );
    const isAreaRangeInverted = isInvertedRangeInput(
      areaMinInput,
      areaMaxInput,
      AREA_BOUNDS
    );

    if (isRentRangeInverted || isAreaRangeInverted) {
      return;
    }

    const { rentValue, area } = resolveValidatedRanges();

    const filtersToApply: SearchFilters = { ...filters, rentValue, area };

    setFilters(filtersToApply);
    syncNumericInputsFromFilters(filtersToApply);

    let newUrl = `?UF=${filtersToApply.uf}&city=${filtersToApply.city}`;
    if (filtersToApply.neighborhood)
      newUrl += `&neighborhood=${filtersToApply.neighborhood}`;
    if (filtersToApply.condominium)
      newUrl += `&condominium=${filtersToApply.condominium}`;
    if (filtersToApply.type !== undefined && filtersToApply.type !== null)
      newUrl += `&imovelType=${filtersToApply.type}`;

    if (
      rentValue[0] !== DEFAULT_RENT_RANGE[0] ||
      rentValue[1] !== DEFAULT_RENT_RANGE[1]
    )
      newUrl += `&values=${rentValue.join(",")}`;

    if (
      area[0] !== DEFAULT_AREA_RANGE[0] ||
      area[1] !== DEFAULT_AREA_RANGE[1]
    )
      newUrl += `&area=${area.join(",")}`; 

    if (filtersToApply.total) newUrl += `&total=${filtersToApply.total}`;

    if (filtersToApply.rooms > 0) newUrl += `&rooms=${filtersToApply.rooms}`;
    if (filtersToApply.garages > 0) newUrl += `&garages=${filtersToApply.garages}`;
    if (filtersToApply.bathrooms > 0)
      newUrl += `&bathrooms=${filtersToApply.bathrooms}`;

    if (filtersToApply.furnished)
      newUrl += `&furnished=${filtersToApply.furnished}`;
    if (filtersToApply.has_sea_view)
      newUrl += `&has_sea_view=${filtersToApply.has_sea_view}`;
    if (filtersToApply.has_ac) newUrl += `&has_ac=${filtersToApply.has_ac}`;
    if (filtersToApply.has_porch)
      newUrl += `&has_porch=${filtersToApply.has_porch}`;
    if (filtersToApply.has_storage)
      newUrl += `&has_storage=${filtersToApply.has_storage}`;
    if (filtersToApply.has_elevator)
      newUrl += `&has_elevator=${filtersToApply.has_elevator}`;
    if (filtersToApply.has_gym) newUrl += `&has_gym=${filtersToApply.has_gym}`;
    if (filtersToApply.has_pool) newUrl += `&has_pool=${filtersToApply.has_pool}`;
    if (filtersToApply.has_sports)
      newUrl += `&has_sports=${filtersToApply.has_sports}`;
    if (filtersToApply.has_playground)
      newUrl += `&has_playground=${filtersToApply.has_playground}`;
    if (filtersToApply.has_bbq) newUrl += `&has_bbq=${filtersToApply.has_bbq}`;
    if (filtersToApply.has_party)
      newUrl += `&has_party=${filtersToApply.has_party}`;
    if (filtersToApply.has_game_room)
      newUrl += `&has_game_room=${filtersToApply.has_game_room}`;
    if (filtersToApply.has_garden)
      newUrl += `&has_garden=${filtersToApply.has_garden}`;
    if (filtersToApply.has_24h_concierge)
      newUrl += `&has_24h_concierge=${filtersToApply.has_24h_concierge}`;
    
    if (sortOption !== SortOptions.NO_PREFERENCE) {
      newUrl += `&orderBy=${sortOption}`;
    }

    if (window.location.search !== newUrl)
      history.push(`${window.location.pathname}${newUrl}`);
      
    onSortProperty(sortOption); 
    onClose(); 
  }

  const ThumbToPrice = (props: any, state: any) => {
    const { key, ...otherProps } = props;
    return <StyledThumb key={key} {...otherProps} />;
  };
  const Track = (props: any, state: any) => {
    const { key, ...otherProps } = props;
    return <StyledTrack key={key} {...otherProps} index={state.index} />;
  };
  const ThumbToSize = (props: any, state: any) => {
    const { key, ...otherProps } = props;
    return <StyledThumb key={key} {...otherProps} />;
  };

  function handleFiltersChange<K extends keyof SearchFilters>(
    identifier: K,
    value: SearchFilters[K]
  ) {
    setFilters((prevFilters) => ({ ...prevFilters, [identifier]: value }));
  }

  function handleSelectSortOption(option: SortOptions) {
    if (option === sortOption) setSortOption(SortOptions.NO_PREFERENCE);
    else setSortOption(option);
  }

  function handleRentSliderChange(values: number[]) {
    if (values.length < 2) return;
    handleFiltersChange("rentValue", [values[0], values[1]]);
    setRentMinInput(String(values[0]));
    setRentMaxInput(String(values[1]));
  }

  function handleRentMinInputChange(value: string) {
    setRentMinInput(value);
    setFilters((prev) => ({
      ...prev,
      rentValue: getRentRangeForSlider(value, rentMaxInput, prev.rentValue),
    }));
  }

  function handleRentMaxInputChange(value: string) {
    setRentMaxInput(value);
    setFilters((prev) => ({
      ...prev,
      rentValue: getRentRangeForSlider(rentMinInput, value, prev.rentValue),
    }));
  }

  function handleAreaSliderChange(values: number[]) {
    if (values.length < 2) return;
    handleFiltersChange("area", [values[0], values[1]]);
    setAreaMinInput(String(values[0]));
    setAreaMaxInput(String(values[1]));
  }

  function handleAreaMinInputChange(value: string) {
    setAreaMinInput(value);
    setFilters((prev) => ({
      ...prev,
      area: getAreaRangeForSlider(value, areaMaxInput, prev.area),
    }));
  }

  function handleAreaMaxInputChange(value: string) {
    setAreaMaxInput(value);
    setFilters((prev) => ({
      ...prev,
      area: getAreaRangeForSlider(areaMinInput, value, prev.area),
    }));
  }

  const isRentRangeInverted = isInvertedRangeInput(
    rentMinInput,
    rentMaxInput,
    RENT_BOUNDS
  );
  const isAreaRangeInverted = isInvertedRangeInput(
    areaMinInput,
    areaMaxInput,
    AREA_BOUNDS
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      classNames={{
        modal: "customModal",
      }}
      styles={{
        overlay: { background: "rgba(0, 0, 0, 0)" },
        modalContainer: { textAlign: "right" },
        modal: { maxWidth: `${450 / 16}rem` },
      }}
      closeIcon={<FiX size={30} color="#FFF" />}
    >
      <Container>
        <TitleCloseWrapper>
          <Title>Filtros</Title>
          <FiX onClick={onClose} />
        </TitleCloseWrapper>

        <DefaultWrapper>
          <DefaultTitle>Tipo de imóvel</DefaultTitle>
          <CategoriesCardsWrapper>
            {PROPERTY_TYPES.map((type) => (
              <CategoryCard
                key={type.value}
                $isSelected={filters.type === type.value}
                onClick={() => {
                  const newType =
                    filters.type === type.value ? undefined : type.value;
                  handleFiltersChange("type", newType);
                }}
              >
                {type.label}
              </CategoryCard>
            ))}
          </CategoriesCardsWrapper>
        </DefaultWrapper>

        <DefaultWrapper>
          <SliderContainer>
            <DefaultTitle>Preço</DefaultTitle>
            <ValuesToSearch>
              De {formatValue(filters.rentValue[0])} até{" "}
              {formatValue(filters.rentValue[1])}
            </ValuesToSearch>
            <StyledSlider
              max={20000} 
              min={400} 
              value={[filters.rentValue[0], filters.rentValue[1]]}
              pearling
              step={50}
              renderTrack={Track}
              renderThumb={ThumbToPrice}
              onChange={(e) => {
                if (Array.isArray(e)) {
                  handleRentSliderChange(e);
                }
              }}
              onAfterChange={(e) => {
                if (Array.isArray(e)) {
                  handleRentSliderChange(e);
                }
              }}
            />
          </SliderContainer>
          <InputsWrapper>
            <InputWrapper>
              <InputLabel htmlFor="min-price">Min</InputLabel>
              <InputField
                id="min-price"
                value={rentMinInput}
                type="text"
                inputMode="numeric"
                $hasError={isRentRangeInverted}
                onChange={(e) => handleRentMinInputChange(e.target.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel htmlFor="max-price">Máx</InputLabel>
              <InputField
                id="max-price"
                data-testid="rent-max-input"
                value={rentMaxInput}
                type="text"
                inputMode="numeric"
                $hasError={isRentRangeInverted}
                onChange={(e) => handleRentMaxInputChange(e.target.value)}
              />
            </InputWrapper>
          </InputsWrapper>
          {isRentRangeInverted ? (
            <RangeErrorMessage role="alert">
              {INVERTED_RANGE_ERROR_MESSAGE}
            </RangeErrorMessage>
          ) : null}
          <TotalCheckboxContainer>
            <input
              id="total"
              type="checkbox"
              checked={filters.total}
              onChange={(event) =>
                handleFiltersChange("total", event.target.checked)
              }
            />
            <label htmlFor="total">Incluir IPTU e Condomínio</label>
          </TotalCheckboxContainer>
        </DefaultWrapper>
        <DefaultWrapper>
          <SliderContainer>
            <h3>Área</h3>
            <ValuesToSearch>
              De {filters.area[0]}m² até {filters.area[1]}m²
            </ValuesToSearch>
            <StyledSlider
              max={1000} 
              min={20} 
              value={[filters.area[0], filters.area[1]]}
              pearling
              step={10}
              renderTrack={Track}
              renderThumb={ThumbToSize}
              onChange={(e) => {
                if (Array.isArray(e)) {
                  handleAreaSliderChange(e);
                }
              }}
              onAfterChange={(e) => {
                if (Array.isArray(e)) {
                  handleAreaSliderChange(e);
                }
              }}
            />
          </SliderContainer>
          <InputsWrapper>
            <InputWrapper>
              <InputLabel htmlFor="min-area">Min</InputLabel>
              <InputField
                id="min-area"
                value={areaMinInput}
                type="text"
                inputMode="numeric"
                $hasError={isAreaRangeInverted}
                onChange={(e) => handleAreaMinInputChange(e.target.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel htmlFor="max-area">Máx</InputLabel>
              <InputField
                id="max-area"
                value={areaMaxInput}
                type="text"
                inputMode="numeric"
                $hasError={isAreaRangeInverted}
                onChange={(e) => handleAreaMaxInputChange(e.target.value)}
              />
            </InputWrapper>
          </InputsWrapper>
          {isAreaRangeInverted ? (
            <RangeErrorMessage role="alert">
              {INVERTED_RANGE_ERROR_MESSAGE}
            </RangeErrorMessage>
          ) : null}
        </DefaultWrapper>

        <DefaultWrapper>
          <DefaultTitle>Características</DefaultTitle>
          {CHARACTERISTIC_NAMES.map((characteristic) => {
            const characteristicValue = filters[characteristic.filterName];
            return (
              <CharacteristicNameValueWrapper key={characteristic.label}>
                <CharacteristicName>{characteristic.label}</CharacteristicName>
                <FaMinus
                  className={characteristicValue > 0 ? "active" : undefined}
                  onClick={
                    characteristicValue > 0
                      ? () =>
                          handleFiltersChange(
                            characteristic.filterName,
                            characteristicValue - 1
                          )
                      : () => {}
                  }
                />
                <CharacteristicValue>{characteristicValue}</CharacteristicValue>
                <FaPlus
                  className={characteristicValue < 10 ? "active" : undefined}
                  onClick={
                    characteristicValue < 10
                      ? () =>
                          handleFiltersChange(
                            characteristic.filterName,
                            characteristicValue + 1
                          )
                      : () => {}
                  }
                />
              </CharacteristicNameValueWrapper>
            );
          })}
        </DefaultWrapper>

        <DefaultWrapper>
          <DefaultTitle>Tem no imóvel</DefaultTitle>
          <PropertyBenefitsContent>
            <input
              id="inp-furnished"
              type="checkbox"
              checked={filters.furnished}
              onChange={(event) =>
                handleFiltersChange("furnished", event.target.checked)
              }
            />
            <label htmlFor="inp-furnished">Mobiliado</label>
          </PropertyBenefitsContent>
          <PropertyBenefitsContent>
            <input
              id="inp-sea-view"
              type="checkbox"
              checked={filters.has_sea_view}
              onChange={(event) =>
                handleFiltersChange("has_sea_view", event.target.checked)
              }
            />
            <label htmlFor="inp-sea-view">Vista Mar</label>
          </PropertyBenefitsContent>
          <PropertyBenefitsContent>
            <input
              id="inp-ac"
              type="checkbox"
              checked={filters.has_ac}
              onChange={(event) =>
                handleFiltersChange("has_ac", event.target.checked)
              }
            />
            <label htmlFor="inp-ac">Ar condicionado</label>
          </PropertyBenefitsContent>
          <PropertyBenefitsContent>
            <input
              id="inp-porch"
              type="checkbox"
              checked={filters.has_porch}
              onChange={(event) =>
                handleFiltersChange("has_porch", event.target.checked)
              }
            />
            <label htmlFor="inp-porch">Varanda</label>
          </PropertyBenefitsContent>
          <PropertyBenefitsContent>
            <input
              id="inp-storage"
              type="checkbox"
              checked={filters.has_storage}
              onChange={(event) =>
                handleFiltersChange("has_storage", event.target.checked)
              }
            />
            <label htmlFor="inp-storage">Depósito</label>
          </PropertyBenefitsContent>
        </DefaultWrapper>

        <DefaultWrapper>
          {filters.type !== 2 && ( 
            <>
              <DefaultTitle>Condomínio</DefaultTitle>
              <PropertyBenefitsContent>
                <input
                  id="inp-24h-concierge"
                  type="checkbox"
                  checked={filters.has_24h_concierge}
                  onChange={(event) =>
                    handleFiltersChange("has_24h_concierge", event.target.checked)
                  }
                />
                <label htmlFor="inp-24h-concierge">Portaria 24h</label>
              </PropertyBenefitsContent>
              {filters.type !== 4 && ( 
                <PropertyBenefitsContent>
                  <input
                    id="inp-elevator"
                    type="checkbox"
                    checked={filters.has_elevator}
                    onChange={(event) =>
                      handleFiltersChange("has_elevator", event.target.checked)
                    }
                  />
                  <label htmlFor="inp-elevator">Elevador</label>
                </PropertyBenefitsContent>
              )}
              <PropertyBenefitsContent>
                <input
                  id="inp-gym"
                  type="checkbox"
                  checked={filters.has_gym}
                  onChange={(event) =>
                    handleFiltersChange("has_gym", event.target.checked)
                  }
                />
                <label htmlFor="inp-gym">Academia</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-pool"
                  type="checkbox"
                  checked={filters.has_pool}
                  onChange={(event) =>
                    handleFiltersChange("has_pool", event.target.checked)
                  }
                />
                <label htmlFor="inp-pool">Piscina</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-sports"
                  type="checkbox"
                  checked={filters.has_sports}
                  onChange={(event) =>
                    handleFiltersChange("has_sports", event.target.checked)
                  }
                />
                <label htmlFor="inp-sports">Quadra poliesportiva</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-playground"
                  type="checkbox"
                  checked={filters.has_playground}
                  onChange={(event) =>
                    handleFiltersChange("has_playground", event.target.checked)
                  }
                />
                <label htmlFor="inp-playground">Playground</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-bbq"
                  type="checkbox"
                  checked={filters.has_bbq}
                  onChange={(event) =>
                    handleFiltersChange("has_bbq", event.target.checked)
                  }
                />
                <label htmlFor="inp-bbq">Churrasqueira</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-party"
                  type="checkbox"
                  checked={filters.has_party}
                  onChange={(event) =>
                    handleFiltersChange("has_party", event.target.checked)
                  }
                />
                <label htmlFor="inp-party">Salão de festas</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-game-room"
                  type="checkbox"
                  checked={filters.has_game_room}
                  onChange={(event) =>
                    handleFiltersChange("has_game_room", event.target.checked)
                  }
                />
                <label htmlFor="inp-game-room">Salão de jogos</label>
              </PropertyBenefitsContent>
              <PropertyBenefitsContent>
                <input
                  id="inp-garden"
                  type="checkbox"
                  checked={filters.has_garden}
                  onChange={(event) =>
                    handleFiltersChange("has_garden", event.target.checked)
                  }
                />
                <label htmlFor="inp-garden">Jardim</label>
              </PropertyBenefitsContent>
            </>
          )}
        </DefaultWrapper>

        <DefaultWrapper>
          <DefaultTitle>Ordenar por</DefaultTitle>
          <CategoriesCardsWrapper>
            {ORDER_BY_OPTIONS.map((option) => (
              <CategoryCard
                key={option}
                $isSelected={option === sortOption}
                onClick={() => handleSelectSortOption(option)}
              >
                {option}
              </CategoryCard>
            ))}
          </CategoriesCardsWrapper>
        </DefaultWrapper>
        <ButtonsContainer>
          <CustomButton
            onClick={onClose}
            size="large"
            style={{
              background: "hsl(219, 100%, 99%)",
              color: "hsl(0, 0%, 52%)",
            }}
          >
            Cancelar
          </CustomButton>
          <CustomButton size="large" onClick={handleApplyFilters}>
            Aplicar
          </CustomButton>
        </ButtonsContainer>
      </Container>
    </Modal>
  );
}